import React, {useState} from 'react';
import {graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {Button} from '@/web-components';
import Util from '../../forecast-app/shared/util/util';
import BackfillClientContacts from '../../mutations/mission_control/backfill_client_contacts';
import {createToast} from '../../forecast-app/shared/components/toasts/toast';
import MissionControlHeader from './mission_control_header';

// The company service handles one bounded chunk per call, so the page walks the estate itself
// rather than holding a single request open across every company.
const CHUNK_SIZE = 25;

const ClientContactsBackfill = ({viewer}) => {
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState(null);
	const [progress, setProgress] = useState(null);

	// retry=false: a retried mutation starts a second concurrent sweep over the same companies
	// while the first is still running server-side, and client_contact has no unique index on
	// (client_id, email) to absorb the resulting double insert.
	const runChunk = (dryRun, afterCompanyId) =>
		new Promise((resolve, reject) => {
			Util.CommitMutation(
				BackfillClientContacts,
				{dryRun, afterCompanyId, limit: CHUNK_SIZE},
				response => resolve(response.backfillClientContacts),
				false,
				null,
				reject
			);
		});

	const run = async dryRun => {
		if (
			!dryRun &&
			!window.confirm(
				'Run the Contacts back-fill for ALL companies? This creates client_contact rows for existing guest persons. It is idempotent and safe to re-run.'
			)
		) {
			return;
		}
		setRunning(true);
		setResult(null);

		const totals = {companiesProcessed: 0, companiesFailed: 0, contactsCreated: 0, dryRun};
		let afterCompanyId = 0;

		try {
			// Terminates on an empty chunk. The cursor only moves forward, so a company that
			// yields nothing insertable cannot wedge the walk.
			for (;;) {
				const r = await runChunk(dryRun, afterCompanyId);
				if (!r) {
					setProgress(null);
					break;
				}
				// Accumulate before testing for the end, so a chunk's failures always reach the
				// totals the operator is shown.
				totals.companiesProcessed += r.companiesProcessed;
				totals.companiesFailed += r.companiesFailed;
				totals.contactsCreated += r.contactsCreated;
				// Empty chunk means done. Test processed + failed rather than processed alone: the
				// server only counts a company as processed when it succeeded, so a chunk whose
				// companies all threw reports 0 processed with the rest of the estate still ahead.
				if (r.companiesProcessed + r.companiesFailed === 0) {
					setProgress(null);
					break;
				}
				afterCompanyId = r.lastCompanyId;
				setProgress({...totals, companiesRemaining: r.companiesRemaining});
			}

			setResult(totals);
			createToast({
				duration: 8000,
				message: `${dryRun ? 'Dry run' : 'Back-fill'} complete — ${totals.companiesProcessed} companies processed, ${
					totals.companiesFailed
				} failed, ${totals.contactsCreated} contacts ${dryRun ? 'would be created' : 'created'}.`,
			});
		} catch (e) {
			// Nothing to unwind. Contacts are committed per batch inside the company sync, so whatever
			// was written stays written. There is no stored cursor either — a later run rediscovers
			// the outstanding companies, because a company with no remaining guests stops matching.
			setResult({...totals, interrupted: true});
			createToast({
				duration: 8000,
				message: `${dryRun ? 'Dry run' : 'Back-fill'} stopped after ${
					totals.companiesProcessed
				} companies. Contacts already created are saved — run again to continue.`,
			});
		} finally {
			setRunning(false);
			setProgress(null);
		}
	};

	return (
		<>
			<MissionControlHeader viewer={viewer} />
			<div className="mission-control-client-contacts-backfill" style={{padding: 24, maxWidth: 720}}>
				<h2>Contacts GA back-fill</h2>
				<p>
					Creates <code>client_contact</code> rows for existing guest persons across all companies. Reuses the
					per-company sync, so it is idempotent and safe to re-run. Use <strong>Dry run</strong> first to preview the
					count.
				</p>
				<p>
					Runs in chunks of {CHUNK_SIZE} companies. Contacts are written in batches and committed as it goes, so
					nothing is lost if this is interrupted — run it again and it skips the companies already done and continues
					with the rest. Closing this page stops the run; it does not undo anything.
				</p>
				<div style={{display: 'flex', gap: 12, marginTop: 16}}>
					<Button
						size={Button.SIZE.SMALL}
						variant={Button.VARIANT.PURPLE_FILLED}
						disabled={running}
						onClick={() => run(true)}
					>
						Dry run
					</Button>
					<Button
						size={Button.SIZE.SMALL}
						variant={Button.VARIANT.PURPLE_FILLED}
						disabled={running}
						onClick={() => run(false)}
					>
						Run back-fill
					</Button>
				</div>
				{progress && (
					<div style={{marginTop: 24}}>
						<strong>Running…</strong>
						<ul>
							<li>Companies processed so far: {progress.companiesProcessed}</li>
							<li>Companies remaining: {progress.companiesRemaining}</li>
							<li>
								Contacts {progress.dryRun ? 'counted' : 'created'} so far: {progress.contactsCreated}
							</li>
						</ul>
					</div>
				)}
				{result && (
					<div style={{marginTop: 24}}>
						<strong>
							{result.dryRun ? 'Dry run' : 'Back-fill'} {result.interrupted ? 'stopped' : 'result'}
						</strong>
						<ul>
							<li>Companies processed: {result.companiesProcessed}</li>
							<li>Companies failed: {result.companiesFailed}</li>
							<li>
								Contacts {result.dryRun ? 'that would be created' : 'created'}: {result.contactsCreated}
							</li>
						</ul>
						{result.interrupted && (
							<p>
								Contacts already created are saved. Run again to continue with the companies still outstanding.
							</p>
						)}
					</div>
				)}
			</div>
		</>
	);
};

const clientContactsBackfillQuery = graphql`
	query clientContactsBackfill_Query {
		viewer {
			actualPersonId
			component(name: "control_client_contacts_backfill")
			...missionControlHeader_viewer
		}
	}
`;

export {clientContactsBackfillQuery};

export default withRouter(ClientContactsBackfill);
